/**
 * Application Filters Component
 * Filter and search controls for applications
 */

import React, { useState, useEffect, useCallback } from 'react';
import { APPLICATION_STATUS } from '../../config/constants';
import './ApplicationFilters.css';

const ApplicationFilters = ({
  searchParams,
  onSearch,
  isProcessing
}) => {
  const [localSearchParams, setLocalSearchParams] = useState(searchParams);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Update local state when props change
  useEffect(() => {
    setLocalSearchParams(searchParams);
  }, [searchParams]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Handle search input change with debouncing
  const handleSearchInputChange = useCallback((value) => {
    const newParams = {
      ...localSearchParams,
      search: value
    };
    setLocalSearchParams(newParams);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      onSearch(newParams);
    }, 500); // 500ms debounce
    
    setSearchTimeout(timeout);
  }, [localSearchParams, searchTimeout, onSearch]);

  // Handle filter changes (immediate)
  const handleFilterChange = (name, value) => {
    const newParams = {
      ...localSearchParams,
      [name]: value
    };
    setLocalSearchParams(newParams);
    // Trigger search immediately for filter changes
    onSearch(newParams);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    onSearch(localSearchParams);
  };

  // Handle reset (not used in new layout)
  // const handleReset = () => {
  //   const resetParams = {
  //     search: '',
  //     status: '',
  //     state: '',
  //     city: '',
  //     created_after: '',
  //     created_before: '',
  //     ordering: '-created_at'
  //   };
  //   setLocalSearchParams(resetParams);
  //   onSearch(resetParams);
  // };

  // Handle clear filters
  const handleClearFilters = () => {
    const clearedParams = {
      ...localSearchParams,
      status: '',
      state: '',
      city: '',
      created_after: '',
      created_before: ''
    };
    setLocalSearchParams(clearedParams);
    onSearch(clearedParams);
  };

  return (
    <div className="filters-section">
      <div className="search-filters-grid">
        {/* Quick Search - Left Side */}
        <div className="search-section">
          <div className="form-group">
            <label className="form-label">Quick Search</label>
            <div className="search-input-container">
              <input
                type="text"
                className="form-input"
                placeholder="Search applications..."
                value={localSearchParams.search}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                disabled={isProcessing}
              />
              <button 
                className="search-button"
                onClick={handleSearch}
                type="button"
                disabled={isProcessing}
              >
                🔍
              </button>
            </div>
          </div>
        </div>

        {/* Quick Filters - Right Side */}
        <div className="filters-section-right">
          <div className="form-group">
            <label className="form-label">Quick Filters</label>
            <div className="quick-filters">
              <button 
                className={`quick-filter-btn ${localSearchParams.status === APPLICATION_STATUS.PENDING ? 'active' : ''}`}
                onClick={() => handleFilterChange('status', localSearchParams.status === APPLICATION_STATUS.PENDING ? '' : APPLICATION_STATUS.PENDING)}
                type="button"
                disabled={isProcessing}
              >
                Pending
              </button>
              <button 
                className={`quick-filter-btn ${localSearchParams.status === APPLICATION_STATUS.APPROVED ? 'active' : ''}`}
                onClick={() => handleFilterChange('status', localSearchParams.status === APPLICATION_STATUS.APPROVED ? '' : APPLICATION_STATUS.APPROVED)}
                type="button"
                disabled={isProcessing}
              >
                Approved
              </button>
              <button 
                className={`quick-filter-btn ${localSearchParams.status === APPLICATION_STATUS.REJECTED ? 'active' : ''}`}
                onClick={() => handleFilterChange('status', localSearchParams.status === APPLICATION_STATUS.REJECTED ? '' : APPLICATION_STATUS.REJECTED)}
                type="button"
                disabled={isProcessing}
              >
                Rejected
              </button>
              <button 
                className={`quick-filter-btn ${localSearchParams.state === 'VIC' ? 'active' : ''}`}
                onClick={() => handleFilterChange('state', localSearchParams.state === 'VIC' ? '' : 'VIC')}
                type="button"
                disabled={isProcessing}
              >
                Victoria
              </button>
              <button 
                className={`quick-filter-btn ${localSearchParams.state === 'NSW' ? 'active' : ''}`}
                onClick={() => handleFilterChange('state', localSearchParams.state === 'NSW' ? '' : 'NSW')}
                type="button"
                disabled={isProcessing}
              >
                NSW
              </button>
              <button 
                className={`quick-filter-btn ${localSearchParams.state === 'QLD' ? 'active' : ''}`}
                onClick={() => handleFilterChange('state', localSearchParams.state === 'QLD' ? '' : 'QLD')}
                type="button"
                disabled={isProcessing}
              >
                Queensland
              </button>
              <button 
                className={`quick-filter-btn ${localSearchParams.state === 'WA' ? 'active' : ''}`}
                onClick={() => handleFilterChange('state', localSearchParams.state === 'WA' ? '' : 'WA')}
                type="button"
                disabled={isProcessing}
              >
                WA
              </button>
              <button 
                className={`quick-filter-btn ${Object.values(localSearchParams).every(v => v === '' || v === localSearchParams.search) ? 'active' : ''}`}
                onClick={handleClearFilters}
                type="button"
                disabled={isProcessing}
              >
                All Applications
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationFilters;
